import { PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import React, { useContext, useEffect, useState } from "react";
import "./QuotationInvoice.css";
import "./JapanQuotationTemplate.css";
import { makePdf } from "../PrintQuotation/PrintQuotation";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { QuotationInfoType } from "../Quotation.types";
import { QuotationContext } from "../QuotationContext";
import { CountryProfileService } from "../../../services/CountryProfiles/CountryProfileService";
import { useNavigate } from "react-router-dom";
import { priceWithCurrencyForJapan, symbolForCurrency } from "../../../Utils/PriceWithCurrency";
import { CountryProfileInfo } from "../../CountriesProfile/CountriesProfile.type";
import { IExtendedDetailItemEntity } from "../Common/Interfaces/IItemEntity";
import { formattedPriceForJapan } from "../../../Utils/FormattedPrice";
import { formatDateWithPattern, getCountryDateFormat } from "../../../Utils/DateFormatUtility";

// Helper function for country-specific date formatting
const formatDateForCountry = async (dateStr: string | undefined, countryName: string | undefined): Promise<string> => {
  if (!dateStr) return '';
  
  try {
    // Parse the date string
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateStr}`);
      return dateStr || '';
    }
    
    // For Japan, the default format should be YYYY/MM/DD
    let dateFormat = 'YYYY/MM/DD'; // Default format for Japan
    if (countryName) {
      const countryFormat = await getCountryDateFormat(countryName);
      if (countryFormat) {
        dateFormat = countryFormat;
      }
    }
    
    // Format the date according to country preference
    return formatDateWithPattern(date, dateFormat);
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateStr || '';
  }
};

// Japanese language labels and translations
const JapaneseLabels = {
  // Header
  quotation: "見積書",
  microsoft: "Microsoft",
  
  // Company Info
  companyInfo: {
    name: "日本マイクロソフト株式会社",
    address1: "品川グランドセントラルタワー",
    address2: "東京都港区港南2-16-3",
    address3: "〒108-0075 東京都",
    taxId: "登録番号"
  },
  
  // Quote Details
  quoteDetails: {
    quotationId: "お見積番号",
    effectiveFrom: "発行日",
    effectiveTo: "有効期限",
    quotationDate: "見積書日付",
    expiryDate: "有効期限"
  },
  
  // Customer Information
  customerInfo: {
    companyName: "会社名",
    accountNumber: "お客様 ID",
    contactName: "連絡先名",
    contactEmail: "連絡先メール",
    phone: "電話番号",
    customerPo: "お客様注文番号",
    opportunityId: "Opportunity ID"
  },
  
  // Microsoft Account Info
  microsoftAccount: {
    accountLead: "マイクロソフト担当者",
    accountLeadEmail: "担当者メール",
    accountLeadPhone: "担当者電話番号"
  },
  
  // Address Information
  addresses: {
    billTo: "請求先住所",
    shipTo: "配送先住所"
  },
  
  // Product Table Headers
  productTable: {
    itemNumber: "品目番号",
    description: "製品名",
    partNumber: "型番", 
    quantity: "数量",
    Price: "価格",
    discountAmount: "割引額",
    discountPercentage: "割引率",
    unitPrice: "単価",
    lineTotal: "合計",
    tax: "消費税額",
    fee: "手数料"
  },
  
  // Summary Labels
  summary: {
    subtotal: "小計",
    totalDiscount: "総割引額",
    totalTax: "消費税合計",
    totalFee: "手数料合計", 
    grandTotal: "総合計",
    currency: "通貨"
  },
  
  // Payment Information
  payment: {
    paymentTerms: "支払条件",
    paymentMethod: "支払方法"
  },
  
  // Footer
  footer: {
    termsConditions: "利用規約",
    thankYou: "お取引いただき、ありがとうございます。",
    validityNote: "この見積書は有効期限内のみ有効です。",
    contactNote: "ご質問がございましたら、担当者までお気軽にお問い合わせください。",
    customerSignature: "お客様承認サイン",
    date: "日付",
    termsNote: "見積書に添付されている条件をご確認ください。発注書の発行または購入価格のお支払いにより、製品に関してここに添付されているビジネス販売条件に同意されたものとみなされます。",
    learnMore: "詳細については、こちらをクリック"
  },
  
  // Buttons
  buttons: {
    generatePdf: "PDF見積書を生成",
    back: "戻る",
    print: "印刷"
  }
};

export const JapanQuotationTemplate: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const appInsights = useAppInsightsContext();
  const [termsAndConditionsDocument, settermsAndConditionsDocument] = useState(
    new Uint8Array()
  );
  const [countryProfileInfo, setCountryProfileInfo] =
    useState<CountryProfileInfo>();
  const [termsAndConditionsDocumentId, settermsAndConditionsDocumentId] =
    useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { quotationInfo } = useContext(QuotationContext) as QuotationInfoType;
  
  // State for formatted dates
  const [formattedQuotationDate, setFormattedQuotationDate] = useState<string>('');
  const [formattedExpiryDate, setFormattedExpiryDate] = useState<string>('');
  const [formattedCurrentDate, setFormattedCurrentDate] = useState<string>('');
  const customerPrimaryContactName =
    quotationInfo?.customer?.primaryContact?.trim() ||
    `${quotationInfo?.customer?.authorizedBuyerFirstName || ""} ${quotationInfo?.customer?.authorizedBuyerLastName || ""}`.trim() ||
    `${quotationInfo?.customer?.selectedBillToContact?.contactFirstName || ""} ${quotationInfo?.customer?.selectedBillToContact?.contactLastName || ""}`.trim() ||
    "";
  
  // Format dates according to Japan's preferred date format
  useEffect(() => {
    const formatDates = async () => {
      try {
        const countryName = "Japan"; // Hard-coded for this template
        
        // Format quotation date
        if (quotationInfo?.quotationDate) {
          const formatted = await formatDateForCountry(quotationInfo.quotationDate, countryName);
          setFormattedQuotationDate(formatted);
        }
        
        // Format expiry date
        if (quotationInfo?.expiryDate) {
          const formatted = await formatDateForCountry(quotationInfo.expiryDate, countryName);
          setFormattedExpiryDate(formatted);
        }
        
        // Format current date
        const formatted = await formatDateForCountry(new Date().toISOString(), countryName);
        setFormattedCurrentDate(formatted);
      } catch (error) {
        console.error('Error formatting dates:', error);
      }
    };

    formatDates();
  }, [quotationInfo?.quotationDate, quotationInfo?.expiryDate]);

  const pdfFileName = `${quotationInfo?.quotationNumber}_Japan_Quotation`;

  const lineItems: IExtendedDetailItemEntity[] =
    quotationInfo?.bundleItems?.length > 0
      ? ([
        ...quotationInfo?.standaloneItems,
        ...quotationInfo?.bundleItems
      ] as IExtendedDetailItemEntity[])
      : quotationInfo?.standaloneItems?.length > 0
        ? ([
          ...quotationInfo?.standaloneItems,
          ...quotationInfo.bundleItems,
        ] as IExtendedDetailItemEntity[])
        : [];

  const PrintHtmlToPdf = async () => {
    appInsights.trackEvent({
      name: "In JapanQuotationTemplate.tsx, Clicked Print button to download the Japan Quote Preview",
    });
    if (
      termsAndConditionsDocument.byteLength === 0 &&
      termsAndConditionsDocumentId
    ) {
      await CountryProfileService.getCountryProfileDocument(
        termsAndConditionsDocumentId
      ).then((res) => {
        appInsights.trackEvent({
          name: "In JapanQuotationTemplate.tsx, Country Terms and Condition document fetched successfully.",
        });
        if (res.data === null) {
          makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
        }
        settermsAndConditionsDocument(res.data);
      })
      .catch((error) => {
        makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
        appInsights.trackException({
          error,
          properties: {
            name: `In JapanQuotationTemplate.tsx, error while fetching termsAndCondition document.`,
          },
        });
      });
    } else {
      makePdf(appInsights, pdfFileName, lineItems.length, termsAndConditionsDocument);
    }
  };

  const backButtonClick = async () => {
    appInsights.trackEvent({
      name: "In JapanQuotationTemplate.tsx, Clicked Back button.",
      properties: { quotationId: quotationInfo.quotationId },
    });
    navigate("/QuotationDetail", {
      state: {
        searchText: quotationInfo.quotationId,
        partitionKey: quotationInfo.partitionKey,
      },
    });
  };

  useEffect(() => {
    async function PrintPdf() {
      try {
        if (termsAndConditionsDocument.byteLength > 0) {
          setIsLoading(true);
          await makePdf(
            appInsights,
            pdfFileName,
            lineItems.length,
            termsAndConditionsDocument
          );
          setIsLoading(false);
          settermsAndConditionsDocument(new Uint8Array());
        }
      } catch (error) {
        setIsLoading(false);
      }
    }
    PrintPdf();
  }, [termsAndConditionsDocument.byteLength]);

  useEffect(() => {
    setIsLoading(true);
    CountryProfileService.getCountryProfileByCountryName(quotationInfo.country)
      .then(async (res) => {
        if (res !== undefined) {
          settermsAndConditionsDocumentId(
            res.data.data.data.termsAndConditions[
              res.data.data.data.termsAndConditions?.length - 1
            ]?.id
          );
          setCountryProfileInfo(res.data.data.data);
          appInsights.trackEvent({
            name: "In JapanQuotationTemplate.tsx, country detail fetched successfully.",
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        appInsights.trackException({
          error,
          properties: {
            name: "In JapanQuotationTemplate.tsx, error while getting all country detail.",
          },
        });
      });
  }, []);

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  return (
    <div className="japan-quotation-template">
      {quotationInfo ? (
        <>
          <div className="top30">
            <PrimaryButton className="float-right" onClick={PrintHtmlToPdf}>
              {JapaneseLabels.buttons.generatePdf}
            </PrimaryButton>
            <PrimaryButton className="float-right" onClick={backButtonClick}>
              {JapaneseLabels.buttons.back}
            </PrimaryButton>
          </div>
          <div style={{ padding: "40px 40px" }}>
            <div id={pdfFileName}>
              {/* Header Section */}
              <div className={`flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'} width100`}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "30px" }}>
                  <div>
                    <div style={{ marginBottom: "20px" }}>
                      <img
                        src="/microsoft-logo.png"
                        className="invlogo"
                        style={{ width: "140px" }}
                        alt="Microsoft logo"
                      />
                    </div>
                    <div>
                      <p className="pb-1">{JapaneseLabels.companyInfo.name}</p>
                      <p>{JapaneseLabels.companyInfo.address1}</p>
                      <p className="pb-1">{JapaneseLabels.companyInfo.address2}</p>
                      <p className="pb-1">{JapaneseLabels.companyInfo.address3}</p>
                      <p className="pb-1" style={{ marginTop: "10px" }}>
                        <b>{JapaneseLabels.companyInfo.taxId}:</b> T2010401092245
                      </p>
                    </div>
                  </div>
                  <div style={{ paddingTop: "50px" }}>
                    <img
                      src="/Japan-stamp.png"
                      style={{ width: "80px", height: "80px" }}
                      alt="Japan Stamp"
                    />
                  </div>
                </div>
                <div style={{ marginTop: "10px" }}>
                  <div>
                    <h1
                      className="pb-1"
                      style={{
                        textAlign: "right",
                        fontSize: "24px",
                        fontWeight: "bold"
                      }}
                    >
                      {JapaneseLabels.quotation}
                    </h1>
                  </div>
                  <div>
                    <p>
                      <b>{JapaneseLabels.quoteDetails.quotationId}:&nbsp;</b>
                      {quotationInfo?.quotationNumber}
                    </p>
                    <p>
                      <b>{JapaneseLabels.quoteDetails.effectiveFrom}:&nbsp;</b>
                      {formattedQuotationDate || quotationInfo?.quotationDate}
                    </p>
                    <p>
                      <b>{JapaneseLabels.quoteDetails.effectiveTo}:&nbsp;</b>
                      {formattedExpiryDate || quotationInfo?.expiryDate}
                    </p>
                  </div>
                </div>
              </div>

              {/* Customer Information Section */}
              <div className={`top30 flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'}`}>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
                  <div className="flexWrap">
                    <p>
                      <b>{JapaneseLabels.customerInfo.companyName}:</b> {` ${quotationInfo?.customer?.name}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>{JapaneseLabels.customerInfo.accountNumber}:</b>
                      {` ${quotationInfo?.customer?.accountId}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>{JapaneseLabels.customerInfo.contactName}:</b>{' '}
                      {customerPrimaryContactName || 'N/A'}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>{JapaneseLabels.customerInfo.contactEmail}:</b>
                      {` ${quotationInfo?.customer?.authorizedEmailId}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>{JapaneseLabels.customerInfo.phone}:</b>
                      {` ${quotationInfo?.customer?.authorizedBuyerPhone}`}
                    </p>
                  </div>
                  <div style={{ display: "flex", marginTop: "30px" }}>
                    <p style={{ whiteSpace: "nowrap" }}>
                      <b>{JapaneseLabels.microsoftAccount.accountLead}:</b>
                      {` ${quotationInfo?.microsoftAccountLead}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p style={{ whiteSpace: "nowrap" }}>
                      <b>{JapaneseLabels.microsoftAccount.accountLeadEmail}:</b>
                      {` ${quotationInfo?.microsoftAccountEmail}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>{JapaneseLabels.customerInfo.opportunityId}:</b>
                      {` ${quotationInfo?.msxOpportunityId}`}
                    </p>
                  </div>
                  <div style={{ display: "flex" }}>
                    <p>
                      <b>{JapaneseLabels.customerInfo.customerPo}:</b>
                      {` ${quotationInfo?.customerPurchaseOrder ? quotationInfo?.customerPurchaseOrder : ""}`}
                    </p>
                  </div>
                </div>

                {/* Address Information */}
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}  ${window.innerWidth < 750 ? 'top20' : '0'}`}>
                  <table className="table-no-border">
                    <tr>
                      <td className="td-border">
                        <p>
                          <strong>{JapaneseLabels.addresses.billTo}:</strong>
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedBillToAddress?.name}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedBillToAddress?.street}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedBillToAddress?.city}, {quotationInfo?.customer?.selectedBillToAddress?.province || quotationInfo?.customer?.selectedBillToAddress?.state} {quotationInfo?.customer?.selectedBillToAddress?.postalCode}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedBillToAddress?.country}
                        </p>
                      </td>
                    </tr>
                  </table>
                </div>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}  ${window.innerWidth < 750 ? 'top20' : '0'}`}>
                  <table className="table-no-border">
                    <tr>
                      <td className="td-border">
                        <p>
                          <strong>{JapaneseLabels.addresses.shipTo}:</strong>
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedShipToAddress?.name}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedShipToAddress?.street}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedShipToAddress?.city}, {quotationInfo?.customer?.selectedShipToAddress?.province || quotationInfo?.customer?.selectedShipToAddress?.state} {quotationInfo?.customer?.selectedShipToAddress?.postalCode}
                        </p>
                        <p>
                          {quotationInfo?.customer?.selectedShipToAddress?.country}
                        </p>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>

              {/* Product Table */}
              <div className="top30">
                <table className="table">
                  <thead>
                    <tr className="background-style">
                      <th className="table-border" style={{ textAlign: "left", padding: "10px", fontSize: "12px" }}>
                        {JapaneseLabels.productTable.description}
                      </th>
                      <th className="table-border" style={{ textAlign: "center", padding: "10px", fontSize: "12px" }}>
                        {JapaneseLabels.productTable.partNumber}
                      </th>
                      <th className="table-border" style={{ textAlign: "center", padding: "10px", fontSize: "12px" }}>
                        {JapaneseLabels.productTable.quantity}
                      </th>
                      <th className="table-border msrp-column" style={{ textAlign: "right", padding: "10px", fontSize: "12px" }}>
                        {JapaneseLabels.productTable.Price}
                      </th>
                      <th className="table-border discount-amount-column" style={{ textAlign: "right", padding: "10px", fontSize: "12px" }}>
                        {JapaneseLabels.productTable.discountAmount}
                      </th>
                      <th className="table-border discount-percentage-column" style={{ textAlign: "center", padding: "10px", fontSize: "12px" }}>
                        {JapaneseLabels.productTable.discountPercentage}
                      </th>
                      <th className="table-border unit-price-column" style={{ textAlign: "right", padding: "10px", fontSize: "12px" }}>
                        {JapaneseLabels.productTable.unitPrice}
                      </th>
                      <th className="table-border" style={{ textAlign: "right", padding: "10px", fontSize: "12px" }}>
                        {JapaneseLabels.productTable.tax}
                      </th>
                      <th className="table-border" style={{ textAlign: "right", padding: "10px", fontSize: "12px" }}>
                        {JapaneseLabels.productTable.lineTotal}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems?.map((product, index) => (
                      <tr key={index}>
                        <td className="table-border" style={{ textAlign: "left", padding: "8px", fontSize: "11px" }}>
                          {product.productTitle}
                        </td>
                        <td className="table-border" style={{ textAlign: "center", padding: "8px", fontSize: "11px" }}>
                          {product.sapItemId}
                        </td>
                        <td className="table-border" style={{ textAlign: "center", padding: "8px", fontSize: "11px" }}>
                          {product.quantity}
                        </td>
                        <td className="table-border text-right msrp-column" style={{ padding: "8px", fontSize: "11px" }}>
                          {formattedPriceForJapan(product.msrp || product.unitPrice)}
                        </td>
                        <td className="table-border text-right discount-amount-column" style={{ padding: "8px", fontSize: "11px" }}>
                          {formattedPriceForJapan((product.discountedPrice || 0) / (product.quantity || 1))}
                        </td>
                        <td className="table-border discount-percentage-column" style={{ textAlign: "center", padding: "8px", fontSize: "11px" }}>
                          {product.discountedPercentage ? `${product.discountedPercentage}%` : "0%"}
                        </td>
                        <td className="table-border text-right unit-price-column" style={{ padding: "8px", fontSize: "11px" }}>
                          {formattedPriceForJapan(product.unitPrice)}
                        </td>
                        <td className="table-border text-right" style={{ padding: "8px", fontSize: "11px" }}>
                          {formattedPriceForJapan(product.lineItemTax)}
                        </td>
                        <td className="table-border text-right" style={{ padding: "8px", fontSize: "11px" }}>
                          {formattedPriceForJapan(product.lineTotal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary Section */}
              <div className="top30" style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start" }}>
                <div style={{ textAlign: "right", minWidth: "350px" }}>
                  <div style={{ display: "flex", padding: "5px 0" }}>
                    <span style={{ width: "180px", textAlign: "right" }}><strong>送料</strong></span>
                    <span style={{ flex: 1, textAlign: "right" }}><strong>{priceWithCurrencyForJapan(quotationInfo?.quoteCurrency, "0")}</strong></span>
                  </div>
                  <div style={{ display: "flex", padding: "5px 0" }}>
                    <span style={{ width: "180px", textAlign: "right" }}><strong>小計</strong></span>
                    <span style={{ flex: 1, textAlign: "right" }}><strong>{priceWithCurrencyForJapan(quotationInfo?.quoteCurrency, quotationInfo?.subTotal)}</strong></span>
                  </div>
                  <div style={{ display: "flex", padding: "5px 0" }}>
                    <span style={{ width: "180px", textAlign: "right" }}><strong>消費税額(税率)</strong></span>
                    <span style={{ flex: 1, textAlign: "right" }}><strong>{priceWithCurrencyForJapan(quotationInfo?.quoteCurrency, quotationInfo?.totalTax)} (10%)</strong></span>
                  </div>
                  <div style={{ display: "flex", padding: "5px 0" }}>
                    <span style={{ width: "180px", textAlign: "right" }}><strong>総合計</strong></span>
                    <span style={{ flex: 1, textAlign: "right" }}><strong>{priceWithCurrencyForJapan(quotationInfo?.quoteCurrency, quotationInfo?.total)}</strong></span>
                  </div>
                  <div style={{ display: "flex", padding: "5px 0" }}>
                    <span style={{ width: "180px", textAlign: "right" }}><strong>通貨</strong></span>
                    <span style={{ flex: 1, textAlign: "right" }}><strong>{quotationInfo?.quoteCurrency} {symbolForCurrency(quotationInfo?.quoteCurrency)}</strong></span>               
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="top50" style={{ paddingTop: "20px" }}>
                <div style={{ fontSize: "11px", lineHeight: "1.5" }}>
                  <div style={{ marginBottom: "20px" }}>
                    <p>{JapaneseLabels.footer.customerSignature} __________________________</p>
                  </div>
                  <div style={{ marginBottom: "20px" }}>
                    <p>{JapaneseLabels.footer.termsNote}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <Spinner size={SpinnerSize.large} />
      )}
    </div>
  );
};
